import { PropsWithChildren, useContext, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthContext } from 'app/auth';
import { Loader } from '../../components/Loader';
import { LOGIN_REQUIRED_CLIENTS } from '../../consts/authentication';
import { consumeAndRemoveRedirectToFromLocalStorage } from '../../utils/localStorage';
import { getCanonicalPortalSignInRedirect } from '../../utils/portalRedirect';
import {
  getQueryParam,
  isSuccessSigninExternalClientPage,
} from '../../utils/url';
import { QUERY_PARAMS, ROUTES } from '../consts';

export const LoginProtectedRoute = ({ children }: PropsWithChildren) => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const clientQueryParam = getQueryParam(QUERY_PARAMS.INIT_CLIENT) || 'portal';

  // Interactive sign-in only works on the canonical portal (OAuth redirect_uri
  // is registered there); bounce unauthenticated users off a satellite (ST)
  // portal so they never hit `redirect_uri_mismatch`. Authenticated users
  // arrived via the 409 flow and are handled below.
  const canonicalPortalSignInUrl = getCanonicalPortalSignInRedirect();
  const shouldRedirectToCanonicalPortal = !user && !!canonicalPortalSignInUrl;

  const shouldRedirectAuthenticatedUser =
    !!user &&
    !isSuccessSigninExternalClientPage() &&
    !LOGIN_REQUIRED_CLIENTS.includes(clientQueryParam as string);

  useEffect(() => {
    if (shouldRedirectToCanonicalPortal && canonicalPortalSignInUrl) {
      window.location.replace(canonicalPortalSignInUrl);
    }
  }, [shouldRedirectToCanonicalPortal, canonicalPortalSignInUrl]);

  if (shouldRedirectToCanonicalPortal) {
    return <Loader center />;
  }

  if (shouldRedirectAuthenticatedUser) {
    const pendingRedirect = consumeAndRemoveRedirectToFromLocalStorage();

    return (
      <Navigate to={pendingRedirect ?? `${ROUTES.ROOT}${location.search}`} />
    );
  }

  return <>{children}</>;
};
